import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AppContainer} from "../domain/AppContainer";
import './PlayerTable.css';
import {Subscription} from "rxjs";
import {ajax} from "rxjs/ajax";
import {map} from "rxjs/operators";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPrint} from "@fortawesome/free-solid-svg-icons/faPrint";
import {filterNullish} from "../util/operators";
import {ID} from "@datorama/akita";
import {isPlayerUI, Player, PlayerUI} from "../domain/player/models";
import {PlayerFilterableTable} from "./PlayerFilterableTable";
import {ListEventNamesForLeague, ListPlayers} from "../api/endpoints";
import {ResponseWrapper} from "../api/types";

export {
    ACTIVITY_OPTIONS,
    RATING_OPTIONS,
    applyActivityFilter,
    applyRatingFilter,
    sortPlayers,
    formatLastPlayed,
} from "./PlayerFilterableTable";
export type {SortColumn, SortDir} from "./PlayerFilterableTable";

export function applyEventFilter(players: Player[], eventPlayerIds: Set<number> | null): Player[] {
    if (eventPlayerIds === null) return players;
    return players.filter(p => eventPlayerIds.has(Number(p.id)));
}

const EVENT_FILTER_KEY = 'league-manage-event-filter';

interface Props {
    container: AppContainer;
}

export function PlayerTable({container}: Props) {
    const [players, setPlayers] = useState<Player[]>([]);
    const [active, setActive] = useState<Player | undefined>();
    const [numPlayersInMatch, setNumPlayersInMatch] = useState<number>(0);
    const [currentLeague, setCurrentLeague] = useState<ID>(0);
    const [eventNames, setEventNames] = useState<string[]>([]);
    const [eventNameFilter, setEventNameFilterRaw] = useState<string>(
        () => localStorage.getItem(EVENT_FILTER_KEY) ?? ''
    );
    const [eventPlayerIds, setEventPlayerIds] = useState<Set<number> | null>(null);

    useEffect(() => {
        const subs: Subscription[] = [];
        subs.push(container.player.query.selectAll().subscribe(setPlayers));
        subs.push(container.player.query.selectActive().subscribe(setActive));
        subs.push(container.match.query.matchBuilderPlayers$.pipe(map(x => x.length)).subscribe(setNumPlayersInMatch));
        subs.push(container.league.query.active$.pipe(filterNullish(), map(l => l.id)).subscribe(setCurrentLeague));
        return () => { subs.forEach(s => s.unsubscribe()); };
    }, [container.player.query, container.match.query, container.league.query]);

    useEffect(() => {
        if (!currentLeague || currentLeague === 0) return;
        const sub = ajax.getJSON<ResponseWrapper<{eventNames: string[]}>>(ListEventNamesForLeague(currentLeague))
            .subscribe({next: r => setEventNames(r.data?.eventNames ?? []), error: () => {}});
        return () => sub.unsubscribe();
    }, [currentLeague]);

    const setEventNameFilter = useCallback((name: string) => {
        setEventNameFilterRaw(name);
        localStorage.setItem(EVENT_FILTER_KEY, name);
        if (!name) setEventPlayerIds(null);
    }, []);

    useEffect(() => {
        if (eventNames.length > 0 && eventNameFilter && !eventNames.includes(eventNameFilter)) {
            setEventNameFilter('');
        }
    }, [eventNames, eventNameFilter, setEventNameFilter]);

    useEffect(() => {
        if (!eventNameFilter || !currentLeague || currentLeague === 0) {
            setEventPlayerIds(null);
            return;
        }
        const sub = ajax.getJSON<ResponseWrapper<{players: {id: number}[]}>>(`${ListPlayers(currentLeague)}?eventName=${encodeURIComponent(eventNameFilter)}`)
            .subscribe({
                next: r => setEventPlayerIds(new Set((r.data?.players ?? []).map(p => p.id))),
                error: () => setEventPlayerIds(null),
            });
        return () => sub.unsubscribe();
    }, [currentLeague, eventNameFilter]);

    function onRowClick(player: Player) {
        container.player.service.setActive(player.id);
    }

    function renderRowActions(player: Player) {
        const playerUI = container.player.query.ui.getEntity(player.id);
        return (
            <input
                type="checkbox"
                className="add-player-checkbox"
                disabled={numPlayersInMatch >= 2 && !(playerUI as PlayerUI).isInMatchBuilder}
                checked={(isPlayerUI(playerUI) && playerUI.isInMatchBuilder) || false}
                onChange={() => {
                    const ui = container.player.query.ui.getEntity(player.id);
                    if (isPlayerUI(ui) && !ui.isInMatchBuilder) {
                        container.match.service.addToMatch(player);
                    } else {
                        container.match.service.removeFromMatch(player);
                    }
                }}/>
        );
    }

    function renderHeaderActions({activityDays, sortColumn, sortDir, ratingFilter, neverPlayedOnly}: {
        activityDays: number | null;
        sortColumn: string;
        sortDir: string;
        ratingFilter: string;
        neverPlayedOnly: boolean;
    }) {
        const printUrl = `/print/league/${currentLeague}?activity=${activityDays ?? ''}&sortCol=${sortColumn}&sortDir=${sortDir}&ratingFilter=${encodeURIComponent(ratingFilter)}&neverPlayed=${neverPlayedOnly}&eventName=${encodeURIComponent(eventNameFilter)}`;
        return (
            <a
                href={printUrl}
                target="_blank"
                rel="noreferrer"
                className="transparent-border-button-link player-table-header-action">
                <FontAwesomeIcon icon={faPrint} textRendering={"Print"}/> Print
            </a>
        );
    }

    const extraFilter = useMemo(
        () => (ps: Player[]) => applyEventFilter(ps, eventPlayerIds),
        [eventPlayerIds]
    );

    const renderExtraFilters = eventNames.length > 0 ? (
        <label className="player-table-filter-label">
            Event
            <select
                className="player-table-filter-select"
                value={eventNameFilter}
                onChange={e => setEventNameFilter(e.target.value)}>
                <option value="">All Events</option>
                {eventNames.map(name => (
                    <option key={name} value={name}>{name}</option>
                ))}
            </select>
        </label>
    ) : null;

    return (
        <PlayerFilterableTable
            players={players}
            storageNamespace="league-manage"
            defaultActivityDays={null}
            onRowClick={onRowClick}
            activePlayerId={active?.id}
            renderRowActions={renderRowActions}
            renderHeaderActions={renderHeaderActions}
            extraFilter={extraFilter}
            renderExtraFilters={renderExtraFilters}
        />
    );
}
